import { useCallback } from "react";

/* ----------------- COLOR CONVERSION ----------------- */
export const hexToHSL = (hex) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16) / 255;
    g = parseInt(hex.substring(3, 5), 16) / 255;
    b = parseInt(hex.substring(5, 7), 16) / 255;
  }
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

export const hslToHex = (h, s, l) => {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (0 <= h && h < 60) { r = c; g = x; b = 0; }
  else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
  else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
  else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
  else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/* ----------------- MODE GENERATORS ----------------- */
export const generateMonochrome = (hex, count = 5) => {
  const { h, s } = hexToHSL(hex);
  return Array.from({ length: count }, (_, i) => {
    const lightness = 20 + (i * (60 / (count - 1)));
    return hslToHex(h, s, lightness);
  });
};

export const generateComplementary = (hex, count = 5) => {
  const { h, s } = hexToHSL(hex);
  const compHue = (h + 180) % 360;
  return Array.from({ length: count }, (_, i) => {
    const hue = i % 2 === 0 ? h : compHue;
    const lightness = 30 + (i * (40 / (count - 1)));
    return hslToHex(hue, s, lightness);
  });
};

export const generateTriadic = (hex, count = 5) => {
  const { h, s } = hexToHSL(hex);
  const hues = [h, (h + 120) % 360, (h + 240) % 360];
  return Array.from({ length: count }, (_, i) => {
    const hue = hues[i % 3];
    const lightness = 35 + (i * (40 / Math.max(count - 1, 1)));
    return hslToHex(hue, s, lightness);
  });
};

export const generateSplitComplementary = (hex, count = 5) => {
  const { h, s } = hexToHSL(hex);
  const hues = [h, (h + 150) % 360, (h + 210) % 360];
  return Array.from({ length: count }, (_, i) => {
    const hue = hues[i % 3];
    const lightness = 35 + (i * (40 / Math.max(count - 1, 1)));
    return hslToHex(hue, s, lightness);
  });
};

export const generateAnalogous = (hex, count = 5) => {
  const { h, s, l } = hexToHSL(hex);
  const spread = 60;
  const step = spread / (count - 1);
  return Array.from({ length: count }, (_, i) => {
    const newHue = (h - spread / 2 + step * i + 360) % 360;
    return hslToHex(newHue, s, l);
  });
};

/* ----------------- EXPORT FUNCTIONS ----------------- */

export const useExportPalette = (colors) => {

  /* Utility to download text files */
  const copyAsURL = useCallback(() => {
  const url = colors.map(c => c.replace("#", "")).join("-");
  const fullURL = `${window.location.origin}/${url}`;

  navigator.clipboard.writeText(fullURL)
    .then(() => {
      console.log("Palette URL copied to clipboard!");
    })
    .catch((err) => {
      console.error("Failed to copy URL: ", err);
    });
}, [colors]);

  const downloadFile = (content, filename, type = "text/plain") => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  };

  /* =========================
     SVG EXPORT
  ========================== */
  const exportAsSVG = useCallback(() => {
    const svgContent = `<svg width="${colors.length * 100}" height="100" xmlns="http://www.w3.org/2000/svg">
      ${colors
        .map((c, i) =>
          `<rect x="${i * 100}" y="0" width="100" height="100" fill="${c}" />`
        )
        .join("")}
    </svg>`;

    downloadFile(svgContent, "palette.svg", "image/svg+xml");
  }, [colors]);

  /* =========================
     PNG EXPORT
  ========================== */
const exportAsPNG = useCallback(() => {
  const blockWidth = 140;
  const blockHeight = 350;
  const textHeight = 50;

  const canvas = document.createElement("canvas");
  canvas.width = colors.length * blockWidth;
  canvas.height = blockHeight + textHeight;

  const ctx = canvas.getContext("2d");

  colors.forEach((c, i) => {
    const x = i * blockWidth;

    // Draw color block
    ctx.fillStyle = c;
    ctx.fillRect(x, 0, blockWidth, blockHeight);

    // Draw background for text area
    ctx.fillStyle = "#111";
    ctx.fillRect(x, blockHeight, blockWidth, textHeight);

    // Auto contrast text color
    const r = parseInt(c.slice(1, 3), 16);
    const g = parseInt(c.slice(3, 5), 16);
    const b = parseInt(c.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    const textColor = brightness > 150 ? "#fff" : "#fff";

    // Draw hex text
    ctx.fillStyle = textColor;
    ctx.font = "bold 16px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(c.toUpperCase(), x + blockWidth / 2, blockHeight + textHeight / 2);
  });

  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "palette.png";
    link.click();
    URL.revokeObjectURL(url);
  });
}, [colors]);

  /* =========================
     SHAREABLE URL TXT FILE
  ========================== */
  const downloadURL = useCallback(() => {
    const url = colors.map(c => c.replace("#", "")).join("-");
    const fullURL = `${window.location.origin}/palette/${url}`;

    downloadFile(fullURL, "palette-url.txt");
  }, [colors]);

  /* =========================
     TAILWIND V4 EXPORT
  ========================== */
  const downloadTailwindV4 = useCallback(() => {
    const theme = `@theme {\n${colors
      .map((c, i) => `  --color-${i + 1}: ${c};`)
      .join("\n")}\n}`;

    downloadFile(theme, "tailwind-theme.css", "text/css");
  }, [colors]);

  /* =========================
     CSS VARIABLES EXPORT
  ========================== */
  const downloadCSSVariables = useCallback(() => {
    const cssVars = `:root {\n${colors
      .map((c, i) => `  --color-${i + 1}: ${c};`)
      .join("\n")}\n}`;

    downloadFile(cssVars, "palette.css", "text/css");
  }, [colors]);

  /* =========================
     JSON EXPORT
  ========================== */
  const downloadJSON = useCallback(() => {
    const formatted = JSON.stringify(
      { palette: colors },
      null,
      2
    );

    downloadFile(formatted, "palette.json", "application/json");
  }, [colors]);

  return {
    copyAsURL,
    exportAsSVG,
    exportAsPNG,
    downloadURL,
    downloadTailwindV4,
    downloadCSSVariables,
    downloadJSON
  };
};

/* ----------------- CUSTOM HOOK ----------------- */
export const useColorModes = () => {
  const getMonochrome = useCallback((hex, count) => generateMonochrome(hex, count), []);
  const getComplementary = useCallback((hex, count) => generateComplementary(hex, count), []);
  const getAnalogous = useCallback((hex, count) => generateAnalogous(hex, count), []);
  const getTriadic = useCallback((hex, count) => generateTriadic(hex, count), []);
  const getSplitComplementary = useCallback((hex, count) => generateSplitComplementary(hex, count), []);

  return {
    getMonochrome,
    getComplementary,
    getAnalogous,
    getTriadic,
    getSplitComplementary,
    hexToHSL,
  };
};